import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Video, Users, Calendar, Shield } from "lucide-react";
import { getErrorMessage } from "@/lib/errorMessages";
import { validatePassword, PASSWORD_REQUIREMENTS } from "@/lib/security";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Footer } from "@/components/Footer";
import { Navigation } from "@/components/Navigation";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { useTranslation } from "react-i18next";
import { Seo } from "@/components/Seo";

interface AuthProps {
  mode?: "signin" | "signup";
  title?: string;
  description?: string;
  canonicalPath?: string;
}

export default function Auth({
  mode,
  title,
  description,
  canonicalPath,
}: AuthProps) {
  const [authTab, setAuthTab] = useState<"signin" | "signup">(mode ?? "signin");
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [resetEmail, setResetEmail] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isDedicatedRoute = Boolean(mode);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Error checking session:", error);
      } finally {
        setPageLoading(false);
      }
    };

    const params = new URLSearchParams(window.location.search);
    if (params.get("type") === "recovery") {
      setIsResettingPassword(true);
    }

    checkUser();
  }, [navigate]);

  useEffect(() => {
    if (mode) setAuthTab(mode);
  }, [mode]);

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-stream flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin" />
          <p className="text-white">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const passwordValidation = validatePassword(signUpPassword);
    if (!passwordValidation.isValid) {
      toast({
        title: t("auth.errors.weakPasswordTitle"),
        description: passwordValidation.errors[0],
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email: signUpEmail,
        password: signUpPassword,
        options: {
          data: { display_name: displayName },
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      if (error) throw error;
      toast({
        title: t("auth.messages.accountCreatedTitle"),
        description: t("auth.messages.accountCreatedDesc"),
      });
      setSignUpEmail("");
      setSignUpPassword("");
      setDisplayName("");
    } catch (error: any) {
      const { title, description } = getErrorMessage(error);
      toast({ title, description, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: signInEmail,
        password: signInPassword,
      });
      if (error) throw error;
      navigate("/");
    } catch (error: any) {
      const { title, description } = getErrorMessage(error);
      toast({ title, description, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail.trim()) {
      toast({
        title: t("auth.errors.emailRequiredTitle"),
        description: t("auth.errors.emailRequiredDesc"),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/login`,
      });
      if (error) throw error;
      toast({
        title: t("auth.messages.resetEmailTitle"),
        description: t("auth.messages.resetEmailDesc"),
      });
      setResetDialogOpen(false);
      setResetEmail("");
    } catch (error: any) {
      const { title, description } = getErrorMessage(error);
      toast({ title, description, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      toast({
        title: t("auth.errors.weakPasswordTitle"),
        description: passwordValidation.errors[0],
        variant: "destructive",
      });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast({
        title: t("auth.errors.passwordMismatchTitle"),
        description: t("auth.errors.passwordMismatchDesc"),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({
        title: t("auth.messages.passwordUpdatedTitle"),
        description: t("auth.messages.passwordUpdatedDesc"),
      });
      setIsResettingPassword(false);
      setNewPassword("");
      setConfirmNewPassword("");
      navigate("/login");
    } catch (error: any) {
      const { title, description } = getErrorMessage(error);
      toast({ title, description, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const seoTitle = title ?? (authTab === "signup" ? "Create Account - MeetHub" : "Login - MeetHub");
  const seoDescription =
    description ??
    (authTab === "signup"
      ? "Create your MeetHub account and start professional browser-based meetings in minutes."
      : "Sign in to MeetHub to manage meetings, recordings, and team collaboration.");

  return (
    <div className="min-h-screen bg-gradient-soft flex flex-col">
      <Seo title={seoTitle} description={seoDescription} canonicalPath={canonicalPath ?? "/login"} />
      <Navigation />
      <div className="flex-1 flex items-center justify-center p-4 pt-32 pb-10 sm:pt-36">
        <div className="w-full max-w-6xl">
          <div className="grid lg:grid-cols-2 rounded-2xl overflow-hidden border bg-card shadow-lg">
            <div className="relative p-8 sm:p-10 lg:p-12 bg-gradient-primary text-primary-foreground">
              <div className="absolute inset-0 bg-grid opacity-20" aria-hidden />
              <div className="absolute -top-16 -left-16 h-48 w-48 rounded-full bg-white/20 blur-3xl animate-float" aria-hidden />
              <div className="absolute -bottom-20 -right-12 h-56 w-56 rounded-full bg-accent/40 blur-3xl animate-float [animation-delay:1.5s]" aria-hidden />
              <div className="absolute top-1/2 -right-10 h-32 w-32 rounded-full bg-white/15 blur-2xl animate-float [animation-delay:3s]" aria-hidden />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-3 py-1 text-xs font-medium">
                  <Video className="h-3.5 w-3.5" />
                  MeetHub
                </div>
                <h1 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                  {isResettingPassword
                    ? "Secure your account with a new password."
                    : authTab === "signin"
                      ? "Welcome back to your meeting workspace."
                      : "Create your account and start meeting in minutes."}
                </h1>
                <p className="mt-4 text-sm sm:text-base text-primary-foreground/90 max-w-md">
                  {isResettingPassword
                    ? "Choose a strong password to keep your meetings and account protected."
                    : authTab === "signin"
                      ? "Sign in to launch meetings, manage recordings, and keep your team in sync."
                      : "Set up your profile once and run professional browser-based meetings with no setup friction."}
                </p>

                <div className="mt-8 grid sm:grid-cols-2 gap-3">
                  <div className="rounded-xl bg-white/10 border border-white/20 p-4">
                    <Users className="h-5 w-5 mb-2" />
                    <p className="text-sm font-semibold">
                      {authTab === "signin" ? t("auth.features.hdVideo") : "100 participants per room"}
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/10 border border-white/20 p-4">
                    <Calendar className="h-5 w-5 mb-2" />
                    <p className="text-sm font-semibold">
                      {authTab === "signin" ? t("auth.features.scheduler") : "One-click scheduling"}
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/10 border border-white/20 p-4">
                    <Video className="h-5 w-5 mb-2" />
                    <p className="text-sm font-semibold">
                      {authTab === "signin" ? t("auth.features.screenShare") : "Screen share + captions"}
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/10 border border-white/20 p-4">
                    <Shield className="h-5 w-5 mb-2" />
                    <p className="text-sm font-semibold">{t("auth.features.secure")}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 lg:p-10 bg-background">
              {isResettingPassword ? (
                <>
                  <CardHeader className="text-left px-0 pt-0">
                    <CardTitle>{t("auth.setNewPassword")}</CardTitle>
                    <CardDescription>{t("auth.setNewPasswordDesc")}</CardDescription>
                  </CardHeader>
                  <CardContent className="px-0 pb-0">
                    <form onSubmit={handleSetNewPassword} className="space-y-4">
                      <Label htmlFor="new-password">{t("auth.newPassword")}</Label>
                      <PasswordInput id="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                      <PasswordStrengthIndicator password={newPassword} />
                      <Label htmlFor="confirm-new-password">{t("auth.confirmPassword")}</Label>
                      <PasswordInput id="confirm-new-password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} required />
                      <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? t("auth.updating") : t("auth.updatePassword")}
                      </Button>
                    </form>
                  </CardContent>
                </>
              ) : !isDedicatedRoute ? (
                <CardContent className="px-0 pb-0">
                  <Tabs value={authTab} onValueChange={(v) => setAuthTab(v as "signin" | "signup")} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="signin">{t("auth.signIn")}</TabsTrigger>
                      <TabsTrigger value="signup">{t("auth.signUp")}</TabsTrigger>
                    </TabsList>
                    <TabsContent value="signin" className="mt-5">
                      <form onSubmit={handleSignIn} className="space-y-4">
                        <Label htmlFor="signin-email">{t("auth.email")}</Label>
                        <Input id="signin-email" type="email" value={signInEmail} onChange={(e) => setSignInEmail(e.target.value)} required />
                        <Label htmlFor="signin-password">{t("auth.password")}</Label>
                        <PasswordInput id="signin-password" value={signInPassword} onChange={(e) => setSignInPassword(e.target.value)} required />
                        <Button type="submit" className="w-full" disabled={loading}>{loading ? t("auth.signingIn") : t("auth.signIn")}</Button>
                      </form>
                    </TabsContent>
                    <TabsContent value="signup" className="mt-5">
                      <form onSubmit={handleSignUp} className="space-y-4">
                        <Label htmlFor="display-name">{t("auth.displayName")}</Label>
                        <Input id="display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
                        <Label htmlFor="signup-email">{t("auth.email")}</Label>
                        <Input id="signup-email" type="email" value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} required />
                        <Label htmlFor="signup-password">{t("auth.password")}</Label>
                        <PasswordInput id="signup-password" value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} required />
                        <PasswordStrengthIndicator password={signUpPassword} />
                        <Button type="submit" className="w-full" disabled={loading}>{loading ? t("auth.creatingAccount") : t("auth.signUp")}</Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              ) : authTab === "signin" ? (
                <CardContent className="px-0 pb-0">
                  <CardHeader className="text-left px-0 pt-0">
                    <CardTitle>{t("auth.signIn")}</CardTitle>
                    <CardDescription>{t("auth.welcomeDesc")}</CardDescription>
                  </CardHeader>
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <Label htmlFor="signin-email">{t("auth.email")}</Label>
                    <Input id="signin-email" type="email" value={signInEmail} onChange={(e) => setSignInEmail(e.target.value)} required />
                    <Label htmlFor="signin-password">{t("auth.password")}</Label>
                    <PasswordInput id="signin-password" value={signInPassword} onChange={(e) => setSignInPassword(e.target.value)} required />
                    <Button type="submit" className="w-full" disabled={loading}>{loading ? t("auth.signingIn") : t("auth.signIn")}</Button>
                    <p className="text-sm text-muted-foreground text-center">New to MeetHub? <Link to="/register" className="text-primary hover:underline">Create account</Link></p>
                    <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="link" className="w-full text-sm text-muted-foreground">{t("auth.forgotPassword")}</Button>
                      </DialogTrigger>
                      <DialogContent className="max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>{t("auth.resetPassword")}</DialogTitle>
                          <DialogDescription>{t("auth.resetPasswordDesc")}</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <Label htmlFor="reset-email">{t("auth.email")}</Label>
                          <Input id="reset-email" type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} autoFocus />
                          <Button onClick={handlePasswordReset} className="w-full" disabled={loading}>{loading ? t("auth.sending") : t("auth.sendResetLink")}</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </form>
                </CardContent>
              ) : (
                <CardContent className="px-0 pb-0">
                  <CardHeader className="text-left px-0 pt-0">
                    <CardTitle>{t("auth.signUp")}</CardTitle>
                    <CardDescription>{t("auth.welcomeDesc")}</CardDescription>
                  </CardHeader>
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <Label htmlFor="display-name">{t("auth.displayName")}</Label>
                    <Input id="display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
                    <Label htmlFor="signup-email">{t("auth.email")}</Label>
                    <Input id="signup-email" type="email" value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} required />
                    <Label htmlFor="signup-password">{t("auth.password")}</Label>
                    <PasswordInput id="signup-password" value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} required />
                    <PasswordStrengthIndicator password={signUpPassword} />
                    <Button type="submit" className="w-full" disabled={loading}>{loading ? t("auth.creatingAccount") : t("auth.signUp")}</Button>
                    <p className="text-sm text-muted-foreground text-center">Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link></p>
                  </form>
                </CardContent>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
